import {
  useDeleteSourceApi,
  useDownloadSourceFileApi,
  useGetPresignedUrlsApi,
  useKnowledgeGroupsApi,
  useRegisterFilesApi,
  useSourcesApi,
} from '@/api/sources';
import { useAuthenticatedClient } from '@/api/useApi';
import type { FileSummaryDto, FileVisibility } from '@ditto/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

export const useSourcesService = () => {
  const { data, ...rest } = useSourcesApi();
  const sources = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { sources, ...rest };
};

export const useSourcesWithRefetchService = () => {
  const [processingFileIds, setProcessingFileIds] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data, queryKey, refetch, ...rest } = useSourcesApi();

  const sources = useMemo(() => {
    return data ?? [];
  }, [data]);

  // Manual refetch interval when tracking files
  useEffect(() => {
    if (processingFileIds.length === 0) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey });
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [processingFileIds.length, queryClient, queryKey]);

  // Auto-clear completed files from tracking
  useEffect(() => {
    if (processingFileIds.length === 0 || !data) return;

    const stillProcessing = processingFileIds.filter(id => {
      const source = data.find(s => s.id === id);
      // Keep tracking if:
      // - File doesn't exist in data yet (waiting for refetch to include it)
      // - File exists and is still processing
      if (!source) return true;
      return source.status === 'uploading' || source.status === 'embedding';
    });

    if (stillProcessing.length !== processingFileIds.length) {
      setProcessingFileIds(stillProcessing);
    }
  }, [data, processingFileIds]);

  const startTrackingFiles = (fileIds: string[]) => {
    setProcessingFileIds(fileIds);
  };

  const queryCache = queryClient.getQueryCache();

  const processingFilesPromise = (uploadedFileIds: string[]) =>
    new Promise<void>((resolve, reject) => {
      const unsubscribe = queryCache.subscribe(() => {
        const cache = queryCache.find<typeof data>({ queryKey });

        const sources =
          cache?.state.data?.filter(s => uploadedFileIds.includes(s.id)) ?? [];

        const isLoading = sources.some(
          s => s.status === 'uploading' || s.status === 'embedding'
        );

        const isError = sources.some(s => s.status === 'error');

        if (isError) {
          unsubscribe();
          reject();
        }

        if (!isLoading) {
          unsubscribe();
          resolve();
        }
      });
    });

  return {
    sources,
    processingFilesPromise,
    startTrackingFiles,
    refetch,
    ...rest,
  };
};

export const useKnowledgeGroupsService = () => {
  const { data, ...rest } = useKnowledgeGroupsApi();

  const knowledgeGroups = useMemo(() => {
    return data ?? [];
  }, [data]);

  return { knowledgeGroups, ...rest };
};

// Helper function to upload file to S3
const uploadToS3 = async (file: File, presignedUrl: string): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
};

// Helper function to upload files to S3 in batches
const uploadFilesToS3InBatches = async (
  files: File[],
  uploadUrls: Array<{ key: string; url: string; fileName: string }>
): Promise<{
  uploadResults: Array<{ success: boolean; file: File; error?: string }>;
  completedUploads: Array<{
    s3Key: string;
    fileName: string;
    size: number;
    mimeType: string;
    file: File;
  }>;
}> => {
  const batchSize = 10;
  const uploadResults: Array<{ success: boolean; file: File; error?: string }> =
    [];
  const completedUploads: Array<{
    s3Key: string;
    fileName: string;
    size: number;
    mimeType: string;
    file: File;
  }> = [];

  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize);
    const urlBatch = uploadUrls.slice(i, i + batchSize);

    const batchPromises = batch.map(async (file, index) => {
      const uploadUrl = urlBatch[index];

      try {
        // Upload to S3
        await uploadToS3(file, uploadUrl.url);

        completedUploads.push({
          s3Key: uploadUrl.key,
          fileName: file.name,
          size: file.size,
          mimeType: file.type,
          file,
        });

        return { success: true, file };
      } catch (error) {
        return {
          success: false,
          file,
          error: error instanceof Error ? error.message : 'Upload failed',
        };
      }
    });

    const batchResults = await Promise.allSettled(batchPromises);
    batchResults.forEach(result => {
      if (result.status === 'fulfilled') {
        uploadResults.push(result.value);
      } else {
        uploadResults.push({
          success: false,
          file: batch[0],
          error: result.reason,
        });
      }
    });
  }

  return { uploadResults, completedUploads };
};

export const useFilesUploadService = () => {
  // To upload many files at once, we cannot just send full files data to the backend as this method would not scale with large number of potentially large files
  // Instead, we need to get presigned URLs form our backend for each file and then upload the files to S3 in parallel, directly from the browser
  // Once all files are uploaded, we can register them in the database

  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
  const { isLoaded, isSignedIn } = useAuthenticatedClient();

  const getPresignedUrlsMutation = useGetPresignedUrlsApi();
  const registerFilesMutation = useRegisterFilesApi();

  const uploadFiles = async (files: File[], visibility: FileVisibility) => {
    if (!isLoaded || !isSignedIn) {
      throw new Error('User not authenticated');
    }

    setUploadingFiles(files);

    try {
      // Step 1: Get presigned URLs for all files
      const fileInfos = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
      }));

      const uploadUrls = await getPresignedUrlsMutation.mutateAsync(fileInfos);

      // Step 2: Upload files to S3 in parallel (batched)
      const { uploadResults, completedUploads } =
        await uploadFilesToS3InBatches(files, uploadUrls);

      // Step 3: Register all successfully uploaded files in database
      let prismaFiles: FileSummaryDto[] = [];
      if (completedUploads.length > 0) {
        prismaFiles = await registerFilesMutation.mutateAsync({
          files: completedUploads.map(upload => ({
            s3Key: upload.s3Key,
            fileName: upload.fileName,
            size: upload.size,
            mimeType: upload.mimeType,
            visibility,
          })),
        });
      }
      return { uploadResults, prismaFiles };
    } finally {
      setUploadingFiles([]);
    }
  };

  const isUploading = uploadingFiles.length > 0;

  return {
    uploadFiles,
    uploadingFiles,
    isUploading,
    error: getPresignedUrlsMutation.error || registerFilesMutation.error,
    isLoaded,
    isSignedIn,
  };
};

export const useDeleteSourceService = () => {
  const { mutate: deleteSource, ...rest } = useDeleteSourceApi();

  return { deleteSource, ...rest };
};

export const useDownloadSourceFileService = (fileId: string | undefined) => {
  const { data: sourceFile, ...rest } = useDownloadSourceFileApi(fileId);
  return {
    sourceFile,
    ...rest,
  };
};
